<i18n
  src="@/locales/views/agro_exporter/finances/harvested_batch_outcomes/show_org_invoice.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/container.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/org_invoice.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/org_invoice_item.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/org_invoice_payment.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/org_invoice_discount.yml" lang="yaml"></i18n>

<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('show_process')"
    :params="{
      agro_exporter_id: agro_exporter_id,
      agro_exporter_process_id: agro_exporter_process_id,
    }"
  >
  </go-back-link>

  <bread-crumbs :labels="[module_name, agro_exporter_process.name]" />

  <template v-if="load_data_general">
    <div class="mt-8">
      <div class="panel">
        <div class="panel-header">
          <h3 class="panel-header-title">
            {{ $t("labels.title") }}
          </h3>
        </div>
        <div class="panel-body">
          <div class="space-y-8 divide-y divide-slate-200">
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3" v-if="org_invoice.harvested_batch">
                <dt class="label-data">
                  {{ $t("model.org_invoice.harvested_batch") }}
                </dt>
                <dd class="content-data">
                  {{ org_invoice.harvested_batch?.batch_code }}
                </dd>
              </div>

              <div class="sm:col-span-3" v-if="org_invoice.invoice_issuer">
                <dt class="label-data">
                  {{ $t("model.org_invoice.invoice_issuer") }}
                </dt>
                <dd class="content-data">
                  {{
                    org_invoice.invoice_issuer?.fullname ||
                    org_invoice.invoice_issuer?.name
                  }}
                </dd>
              </div>

              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.org_invoice.invoice_code") }}
                </dt>
                <dd class="content-data">
                  {{ org_invoice.invoice_code }}
                  <yes-no-label
                    class="mr-1"
                    :value="org_invoice.paid"
                    yes="Pagada"
                    no="No pagada"
                  />
                </dd>
              </div>
              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.org_invoice.invoice_date") }}
                </dt>
                <dd class="content-data">
                  {{ dateFormatter(org_invoice.invoice_date) }}
                </dd>
              </div>

              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.org_invoice.amount") }}
                </dt>
                <dd class="content-data">
                  {{
                    currencyMoney(
                      org_invoice.amount,
                      org_invoice.currency.symbol
                    )
                  }}
                </dd>
              </div>

              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.org_invoice.detraction_total") }}
                </dt>
                <dd class="content-data">
                  {{
                    currencyMoney(
                      org_invoice.detraction_total,
                      org_invoice.currency.symbol
                    )
                  }}
                  ({{
                    numberWithPrecision(
                      Number(org_invoice.detraction_percent) * 100,
                      2
                    )
                  }}%)
                </dd>
              </div>

              <div class="sm:col-span-3" v-if="org_invoice.exchange_rate">
                <dt class="label-data">
                  {{ $t("model.org_invoice.detraction_total") }} (S/.)
                </dt>
                <dd class="content-data">
                  {{
                    org_invoice.exchange_rate
                      ? currencyMoney(
                          numberWithPrecision(
                            Number(org_invoice.detraction_total || 0) *
                              Number(org_invoice.exchange_rate),
                            2
                          ),
                          "S/."
                        ) +
                        "(TC: " +
                        org_invoice.exchange_rate +
                        ")"
                      : ""
                  }}
                </dd>
              </div>
            </div>
          </div>
        </div>
        <div class="panel-footer">
          <div class="panel-footer-actions">
            <destroy-button
              v-if="!proc_button_destroy"
              :disabled_button="false"
              @destroyEvent="destroyExportInvoice()"
              :label="$t('labels.destroy_button_label')"
              :title="
                $t('labels.destroy_button_title', {
                  invoice_code: org_invoice.invoice_code,
                })
              "
              :message="$t('labels.destroy_button_message')"
              :label_confirm="$t('labels.destroy_button_label_confirm')"
            />
            <destroy-button-processing
              v-else
              :label="$t('labels.destroy_button_label')"
            />
            <span class="space-actions"></span>
            <PayOrgInvoiceButton
              :paid="org_invoice.paid"
              @payOrgInvoice="updateOrgInvoice"
            />
            <span class="space-actions"></span>
            <edit-button
              @click.prevent="goToEditExportInvoice()"
              :label="$t('labels.edit_button')"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="mt-8 flex flex-row">
      <h1 class="page-title">
        <i class="fa-solid fa-rectangle-list"></i>
        <span class="space-icon"></span>
        {{ $t("labels.title_org_invoice_items") }}
      </h1>
    </div>
    <div class="mt-8 flex flex-col">
      <div class="table-first-container">
        <div class="table-second-container">
          <div class="table-third-container">
            <table class="table">
              <thead class="table-header">
                <tr>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_item.quantity") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_item.description") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_item.unit_price") }}
                  </th>
                </tr>
              </thead>
              <tbody class="table-body">
                <tr
                  class="table-row"
                  v-for="(invoice_item, idx) in org_invoice.invoice_items"
                  :key="idx"
                >
                  <td class="table-cell">
                    {{ invoice_item.quantity }}
                  </td>
                  <td class="table-cell">
                    {{ invoice_item.description }}
                  </td>
                  <td class="table-cell">
                    {{
                      currencyMoney(
                        invoice_item.unit_price,
                        org_invoice.currency.symbol
                      )
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </template>
  <loading-message v-else />
</template>

<script>
import Requester from "@/views/agro_exporter/requesters/finances/harvested_batch_outcomes/show_org_invoice.js";
import ManageRequester from "@/views/agro_exporter/requesters/finances/harvested_batch_outcomes/manage_org_invoice.js";
import PayOrgInvoiceButton from "@/components/views/agro_exporter/finances/harvested_batch_outcomes/PayOrgInvoiceButton.vue";
export default {
  components: { PayOrgInvoiceButton },
  name: "AeFinancesHarvestedBatchOutcomesShowOrgInvoice",
  props: {
    agro_exporter_process_id: { required: true },
    agro_exporter_id: { required: true },
    org_invoice_id: { required: true },
  },
  data() {
    return {
      load_data_general: false,
      proc_save_button: false,
      proc_button_destroy: false,
      agro_exporter_process: {},
      org_invoice: {},
    };
  },
  methods: {
    loadData: async function () {
      await this.fetchAgroExporterProcess();
      await this.fetchOrgInvoice();
      this.load_data_general = true;
    },
    fetchAgroExporterProcess: async function () {
      let response = await Requester.fetchAgroExporterProcess(
        this.agro_exporter_id,
        this.agro_exporter_process_id
      );
      this.agro_exporter_process = response.data;
    },
    fetchOrgInvoice: async function () {
      let response = await Requester.fetchOrgInvoice(
        this.agro_exporter_id,
        this.org_invoice_id
      );
      this.org_invoice = response.data;
    },

    updateOrgInvoice: async function () {
      this.proc_save_button = true;
      try {
        let response = await ManageRequester.update(
          this.agro_exporter_id,
          this.org_invoice_id,
          {
            paid: !this.org_invoice.paid,
          }
        );
        let org_invoice_update = response.data.org_invoice;
        this.org_invoice.paid = org_invoice_update.paid;
        this.registerSuccessNotification({
          title: response.data.success_message,
        });
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields,
          });
        }
      }
      this.proc_save_button = false;
    },
    destroyExportInvoice: async function () {
      this.proc_button_destroy = true;
      try {
        let response = await ManageRequester.destroy(
          this.agro_exporter_id,
          this.org_invoice_id
        );
        this.registerSuccessNotification({
          title: response.data.success_message,
        });
        this.goToShowProcess();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields,
          });
        }
      }
      this.proc_button_destroy = false;
    },
    goToEditExportInvoice: function () {
      this.$router.push({
        name: this.routeName("edit_invoice"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          agro_exporter_process_id: this.agro_exporter_process_id,
          org_invoice_id: this.org_invoice_id,
        },
      });
    },
    goToShowProcess: function () {
      this.$router.push({
        name: this.routeName("show_process"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          agro_exporter_process_id: this.agro_exporter_process_id,
        },
      });
    },
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          agro_exporter_id: this.agro_exporter_id,
        },
      }).then(() => this.loadData())
    );
  },
};
</script>
